import { REG_EXP_POSITIVE_INTEGER_STRING } from 'shared/entities/regExp/number';
import { IntervalUnit } from 'shared/entities/timeout';
import { ValidatorResult } from 'shared/entities/validator';

import type { CsatSettingsServer } from './server';

export const CONVERSATION_AUTO_CLOSE_DEFAULT_MINUTES = 5;
export const CONVERSATION_AUTO_CLOSE_DEFAULT_UNIT = IntervalUnit.minutes;
export const CONVERSATION_AUTO_CLOSE_MIN_MINUTES = 1;
export const CONVERSATION_AUTO_CLOSE_MAX_MINUTES = 43200;

export const CONVERSATION_AUTO_CLOSE_UNITS = [
  IntervalUnit.minutes,
  IntervalUnit.hours,
  IntervalUnit.days
] as const;

export type ConversationAutoCloseUnit =
  (typeof CONVERSATION_AUTO_CLOSE_UNITS)[number];

const MINUTES_IN_UNIT: Record<ConversationAutoCloseUnit, number> = {
  [IntervalUnit.minutes]: 1,
  [IntervalUnit.hours]: 60,
  [IntervalUnit.days]: 1440
};

const getMinutesInUnit = (unit: ConversationAutoCloseUnit): number =>
  MINUTES_IN_UNIT[unit];

export const getMaxConversationAutoCloseValue = (
  unit: ConversationAutoCloseUnit
): number => CONVERSATION_AUTO_CLOSE_MAX_MINUTES / getMinutesInUnit(unit);

export const convertConversationAutoCloseToMinutes = (
  value: number,
  unit: ConversationAutoCloseUnit
): number => value * getMinutesInUnit(unit);

export const validateConversationAutoCloseValue = (
  value: string,
  unit: ConversationAutoCloseUnit
): ValidatorResult => {
  if (!value) {
    return (t) =>
      t('chats.conversationAutoCloseSubsection.validation.required', {
        ns: 'projectSettingsPage'
      });
  }

  const maxValue = getMaxConversationAutoCloseValue(unit);
  const numberValue = Number(value);

  if (
    !REG_EXP_POSITIVE_INTEGER_STRING.test(value) ||
    numberValue < 1 ||
    numberValue > maxValue
  ) {
    return (t) =>
      t('chats.conversationAutoCloseSubsection.validation.range', {
        ns: 'projectSettingsPage',
        min: 1,
        max: maxValue
      });
  }

  return null;
};

export const areCsatSettingsEqual = (
  first: CsatSettingsServer,
  second: CsatSettingsServer
): boolean => {
  return (
    first.enabled === second.enabled &&
    first.prompt_text === second.prompt_text &&
    first.ask_comment === second.ask_comment
  );
};
